import { addDisplayName, addProps, flowMax } from 'ad-hok'
import { FC } from 'react'
import { ImageUrl } from 'shared/src/api/types'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'

import { serifFontFamily } from 'utils/styles'
import creatorFeatureLasso from '../assets/creator-feature-lasso.svg'
import { LinkOrButton } from 'components/LinkOrButton'
import { BookmarkButton } from './BookmarkButton'
import { FEED_RODEO_PRODUCT_BOOKMARK_Z_INDEX } from 'utils/zIndex'
import { Views } from './Views'

const WIDTH = 486
const HEIGHT = 503

const xPercentage = (x: number) => `${(x / WIDTH) * 100}%`
const yPercentage = (y: number) => `${(y / HEIGHT) * 100}%`

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    width: '125%',
    alignSelf: 'center',
    aspectRatio: `${WIDTH} / ${HEIGHT}`,
    marginTop: 49,
  },
  lasso: {
    position: 'relative',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  featuredText: {
    color: '#CBA67A',
    position: 'absolute',
    fontFamily: serifFontFamily,
    fontSize: 35,
    fontStyle: 'italic',
    left: xPercentage(64),
    top: -70,
    lineHeight: '140%',
    letterSpacing: '0.01em',
    fontWeight: 800,
    textAlign: 'right',
  },
  feature: {
    position: 'absolute',
    padding: 0,
    background: 'none',
    border: 'none',
    width: xPercentage(140),
    height: yPercentage(180),
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  feature1: {
    left: xPercentage(95),
    top: yPercentage(5),
  },
  feature2: {
    left: xPercentage(274),
    top: yPercentage(39),
  },
  feature3: {
    left: xPercentage(64),
    top: yPercentage(227),
  },
  feature4: {
    left: xPercentage(258),
    top: yPercentage(272),
  },
  bookmark: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: FEED_RODEO_PRODUCT_BOOKMARK_Z_INDEX,
  },
  views: {
    position: 'absolute',
    top: 8,
    left: 8,
    zIndex: FEED_RODEO_PRODUCT_BOOKMARK_Z_INDEX,
  },
})

export interface Feature {
  image: ImageUrl
  navigateTo: string
  alt: string
  bookmarkProductId?: number
  views?: number
}

interface Props {
  name: string
  features: Feature[]
  className?: string
}

export const FeedFeature: FC<Props> = flowMax(
  addDisplayName('FeedFeature'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ name, features, classes, className }) => (
    <div className={cx(classes.container, className)}>
      <img src={creatorFeatureLasso} alt="" className={classes.lasso} />
      <div className={classes.featuredText}>{name}</div>
      {features.map(
        ({ image, navigateTo, alt, bookmarkProductId, views }, index) => (
          <div
            className={cx(
              classes.feature,
              [
                classes.feature1,
                classes.feature2,
                classes.feature3,
                classes.feature4,
              ][index]
            )}
            key={index}
          >
            <LinkOrButton
              navigateTo={navigateTo}
              key={index}
              showBackButton
              additionalState={{ discoverySource: 'feedFeature' }}
            >
              <img src={image.full} alt={alt} className={classes.image} />
            </LinkOrButton>
            {bookmarkProductId && (
              <BookmarkButton
                mode="productTile"
                productId={bookmarkProductId}
                className={classes.bookmark}
              />
            )}
            {views && <Views count={views} className={classes.views} />}
          </div>
        )
      )}
    </div>
  )
)
